import { getMessageStartSeconds } from "@/components/transcript/TimestampDisplay"
import type {
  MetricOutputDisplaySource,
  RunMetricOutput,
} from "@/types/RunMetricOutput"
import type { Message } from "@/types/Transcript"

import { resolveLatencyGaps, type LatencyGap } from "./latencyGaps"
import { LatencyMetricRecord } from "./LatencyMetricRecord"

/** The latency metric whose gaps are the agent's response times. */
const AGENT_RESPONSE_LATENCY_MANAGER = "manager_latency"

/**
 * How far, in seconds, a transcript message may start from the end of a gap
 * and still be the response that ended it. Gaps are measured on the audio, and
 * transcript times come from the conversation events, so the two never agree
 * exactly.
 */
const MAX_ALIGNMENT_SECONDS = 2

const ROLE_BY_SPEAKER: Record<string, Message["role"]> = {
  agent: "assistant",
  assistant: "assistant",
  persona: "user",
  user: "user",
}

interface SpokenMessage {
  index: number
  role: Message["role"]
  start: number
}

function listSpokenMessages(transcript: Message[]): SpokenMessage[] {
  return transcript.flatMap((message, index) => {
    if (message.role !== "assistant" && message.role !== "user") {
      return []
    }
    if (typeof message.content !== "string" || message.content.trim() === "") {
      return []
    }
    const start = getMessageStartSeconds(message)
    return start == null ? [] : [{ index, role: message.role, start }]
  })
}

/**
 * Pins each agent response gap to the message that ended it: the agent message
 * starting closest to the end of the gap. One audio turn can span several
 * transcript messages, and only the first of them carries the gap. A gap with
 * no message close enough is left off rather than guessed at, and so is a gap
 * the agent did not end, whatever role the output says it measured.
 */
function assignLatencyGapsToMessages(
  gaps: LatencyGap[],
  transcript: Message[],
): Map<number, number> {
  const spokenMessages = listSpokenMessages(transcript)
  const claims = new Map<number, { latency: number; distance: number }>()

  for (const gap of gaps) {
    const role = gap.toSpeaker ? ROLE_BY_SPEAKER[gap.toSpeaker] : undefined
    if (role !== "assistant") {
      continue
    }

    let closest: { index: number; distance: number } | null = null
    for (const message of spokenMessages) {
      if (message.role !== role) {
        continue
      }
      const distance = Math.abs(message.start - gap.end)
      if (
        distance <= MAX_ALIGNMENT_SECONDS &&
        (closest === null || distance < closest.distance)
      ) {
        closest = { index: message.index, distance }
      }
    }
    if (!closest) {
      continue
    }

    const existing = claims.get(closest.index)
    if (!existing || closest.distance < existing.distance) {
      claims.set(closest.index, {
        latency: gap.latencySeconds,
        distance: closest.distance,
      })
    }
  }

  return new Map(
    [...claims].map(([index, claim]) => [index, claim.latency] as const),
  )
}

/**
 * The agent's response latency, in seconds, keyed by the transcript message
 * that ended each gap. Only the agent response latency metric has one; any
 * other metric returns null.
 */
export function getMessageLatencies(
  metricRecord: unknown,
  metricOutput: RunMetricOutput | MetricOutputDisplaySource,
  transcript: Message[],
): Map<number, number> | null {
  if (
    !(metricRecord instanceof LatencyMetricRecord) ||
    metricRecord.metric_manager !== AGENT_RESPONSE_LATENCY_MANAGER
  ) {
    return null
  }
  return assignLatencyGapsToMessages(
    resolveLatencyGaps(metricOutput),
    transcript,
  )
}
